'use client';

// Shared subscription / site-ownership logic used by both the nag banner
// (app/components/SubscriptionBanner.tsx) and the hard lockout gate
// (app/components/SubscriptionGate.tsx). Keeping the Chargebee status
// vocabulary, the exempt-installer list, and the `/api/sites` ownership
// lookup in one place means the two features can never drift apart on who
// counts as a paying owner.

import { useEffect, useState } from 'react';
import { useOptionalUser } from '@/context/UserContext';
import {
  SUBSCRIPTION_EXEMPT_INSTALLER_IDS,
  classifySubscriptionSites,
  isActiveLikeStatus,
  isTerminatedStatus,
  resolveBillingOffer,
  type SubscriptionOwnership,
} from '@/app/lib/subscriptionStatus';

// Re-export the pure status helpers so existing consumers can keep importing
// everything subscription-related from this module.
export {
  SUBSCRIPTION_EXEMPT_INSTALLER_IDS,
  isActiveLikeStatus,
  isTerminatedStatus,
  resolveBillingOffer,
};
export type { BillingOffer } from '@/app/lib/subscriptionStatus';

export type OwnershipState = SubscriptionOwnership;

// A lookup that did not produce an answer. Callers that only gate *hiding*
// something (the nag banner) fail open on this via `ownsSite: false`; callers
// that gate *selling* something must branch on `known` and fail closed.
const OWNERSHIP_UNKNOWN: OwnershipState = {
  ownsSite: false,
  exemptByInstaller: false,
  known: false,
};

// Module-level cache of the "does this user own any sites" answer so we don't
// re-fetch /api/sites on every navigation. Keyed by user id because in dev
// you can swap accounts without a full reload.
const ownsSitesCache: Map<number, OwnershipState | Promise<OwnershipState>> = new Map();

// Discard any settled answer for this user so the next lookup re-asks
// /api/sites. In-flight requests are left alone — they are already fresh.
export function invalidateOwnership(userId: number): void {
  const cached = ownsSitesCache.get(userId);
  if (cached && !(cached instanceof Promise)) ownsSitesCache.delete(userId);
}

// `fresh` skips a *settled* cached answer. The cache has no TTL, so a session
// that started before the site was created, claimed, or moved to a bundled
// installer would otherwise keep its stale "not exempt" answer for the whole
// session — fine for deciding what to hide, not fine for deciding what to
// sell. An in-flight promise is still shared: that request is already fresh,
// and joining it avoids a stampede.
export async function fetchOwnership(
  userId: number,
  userEmail?: string | null,
  options?: { fresh?: boolean },
): Promise<OwnershipState> {
  if (options?.fresh) invalidateOwnership(userId);
  const cached = ownsSitesCache.get(userId);
  if (cached) return cached;
  const p = (async () => {
    try {
      const res = await fetch('/api/sites', { credentials: 'include' });
      // A failed lookup is never cached: leaving the rejected promise in the
      // map would pin the whole session to "unknown", so a bundled-installer
      // owner who hit one transient /api/sites error could never get the
      // answer that suppresses the checkout. Dropping it lets a retry (or the
      // next navigation) ask again.
      if (!res.ok) {
        ownsSitesCache.delete(userId);
        return OWNERSHIP_UNKNOWN;
      }
      const data = await res.json();
      // Only an actual array is an answer. This endpoint can return 200 with
      // an error object (`{ detail: ... }`), and coercing that to `[]` would
      // report a confident "owns nothing, not exempt" — indistinguishable
      // from a real self-serve customer, which is precisely the shape that
      // puts the controller checkout back in front of a bundled-installer
      // owner. An empty array stays a valid answer: it genuinely means the
      // user owns no sites.
      const sites = Array.isArray(data)
        ? data
        : (Array.isArray(data?.sites) ? data.sites : null);
      if (!sites) {
        ownsSitesCache.delete(userId);
        return OWNERSHIP_UNKNOWN;
      }
      const result = classifySubscriptionSites(userId, userEmail, sites);
      ownsSitesCache.set(userId, result);
      return result;
    } catch {
      ownsSitesCache.delete(userId);
      return OWNERSHIP_UNKNOWN;
    }
  })();
  ownsSitesCache.set(userId, p);
  return p;
}

// Resolve whether the current user owns a (non-exempt) site. Returns `null`
// until known. Pass `enabled = false` to skip the fetch entirely for the
// majority of users (staff, active subscribers) who can never be locked out —
// they never pay the /api/sites round-trip.
//
// Bump `attempt` to re-run a lookup that came back unknown — the failed
// answer was never cached, so this really does re-ask /api/sites.
//
// Pass `fresh` from anything that gates a purchase: it bypasses a settled
// cached answer so the checkout decision is never made on a result from
// earlier in the session. The nag banner deliberately leaves this off and
// keeps using the cache.
export function useSiteOwnership(
  enabled: boolean,
  attempt = 0,
  fresh = false,
): OwnershipState | null {
  const ctx = useOptionalUser();
  const userId = ctx?.user?.id;
  const userEmail = ctx?.user?.email;
  const [ownership, setOwnership] = useState<OwnershipState | null>(null);

  useEffect(() => {
    if (!enabled || !userId) {
      setOwnership(null);
      return;
    }
    let cancelled = false;
    fetchOwnership(userId, userEmail, { fresh }).then(result => {
      if (!cancelled) setOwnership(result);
    });
    return () => { cancelled = true; };
  }, [enabled, userId, userEmail, attempt, fresh]);

  return enabled ? ownership : null;
}

export type SubscriptionAccessState = 'open' | 'locked';

// The single source of truth for "should this user be locked out of the
// customer data pages". The lockout is driven purely by the user's own
// subscription status — no site-ownership or bundled-installer exemptions:
//   - signed in, and
//   - not staff (admins / installers manage customer accounts and don't hold
//     a personal customer subscription), and
//   - their own subscription status is terminated (cancelled / transferred).
// This is deliberately stricter than the nag banner, which keeps the softer
// ownership / bundled-installer (37/50) exemptions — a cancelled subscription
// is a definitive per-user signal, so we act on it directly and synchronously
// (no /api/sites round-trip, so no flash of data we're about to hide).
export function useSubscriptionAccess(): SubscriptionAccessState {
  const ctx = useOptionalUser();
  const user = ctx?.user;
  if (!user) return 'open';
  if (ctx?.isAdmin || ctx?.isInstaller) return 'open';
  return isTerminatedStatus(user.subscription_status) ? 'locked' : 'open';
}
